import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Login.css';
import brand from '../config/brand';

const Login = () => {
  const [mode, setMode] = useState('login'); // 'login', 'register', 'forgot'
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    registrationCode: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (mode === 'register') {
        await register(formData.username, formData.email, formData.password, formData.registrationCode);
        navigate('/');
      } else if (mode === 'forgot') {
        const res = await fetch('/api/auth/forgot-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: formData.email })
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || 'Failed to send reset link');
        }
        setSuccess(data.message);
      } else {
        await login(formData.email, formData.password);
        navigate('/');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <img
            src={brand.logoUrl}
            alt={brand.name}
            className="login-logo"
          />
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <h2 className="login-form-title">
            {mode === 'register' ? 'Create Account' : mode === 'forgot' ? 'Reset Password' : 'Sign In'}
          </h2>

          {error && <div className="login-error">{error}</div>}
          {success && <div className="login-success">{success}</div>}

          {mode === 'register' && (
            <>
              <div className="form-group">
                <label className="form-label">Registration Code</label>
                <input
                  type="text"
                  name="registrationCode"
                  className="form-input"
                  placeholder="Enter registration code"
                  value={formData.registrationCode}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Username</label>
                <input
                  type="text"
                  name="username"
                  className="form-input"
                  placeholder="Enter your username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                />
              </div>
            </>
          )}

          {mode === 'forgot' && !success && (
            <p className="login-forgot-hint">Enter your email address and we'll send you a link to reset your password.</p>
          )}

          {!success && (
            <>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              {mode !== 'forgot' && (
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      className="form-input"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="btn btn--primary login-btn"
                disabled={loading}
              >
                {loading ? 'Please wait...' : mode === 'register' ? 'Create Account' : mode === 'forgot' ? 'Send Reset Link' : 'Sign In'}
              </button>
            </>
          )}

          {mode === 'login' && (
            <>
              <button
                type="button"
                className="login-forgot-btn"
                onClick={() => switchMode('forgot')}
              >
                Forgot Password?
              </button>
              <p className="login-toggle">
                Don't have an account?
                <button
                  type="button"
                  className="login-toggle-btn"
                  onClick={() => switchMode('register')}
                >
                  Register
                </button>
              </p>
            </>
          )}

          {mode === 'register' && (
            <p className="login-toggle">
              Already have an account?
              <button
                type="button"
                className="login-toggle-btn"
                onClick={() => switchMode('login')}
              >
                Sign In
              </button>
            </p>
          )}

          {mode === 'forgot' && (
            <p className="login-toggle">
              Remember your password?
              <button
                type="button"
                className="login-toggle-btn"
                onClick={() => switchMode('login')}
              >
                Sign In
              </button>
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;
